import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { environment } from '@environment/environment';

export const HTTP_DEVTOOLS_STORAGE_KEY = 'aria_devtools_http_logs';
export const HTTP_DEVTOOLS_MAX_ENTRIES = 50;
export const HTTP_DEVTOOLS_DEFAULT_EXPORT_COUNT = 15;

const MAX_SECTION_LENGTH = 20 * 1024;
const REDACTED = '[redacted]';
const TRUNCATED = '[truncated]';
const SENSITIVE_KEYS = ['authorization', 'cookie', 'set-cookie', 'token', 'password', 'secret', 'apikey', 'accesskey', 'accesstoken', 'refreshtoken'];

export interface HttpDevToolsPayload {
  type: 'http' | 'exception';
  id: string;
  timestamp: string;
  durationMs?: number;
  pending: boolean;
  source?: string;
  method?: string;
  url?: string;
  urlWithParams?: string;
  status?: number;
  statusText?: string;
  ok?: boolean;
  request?: {
    headers: Record<string, unknown>;
    params: Record<string, unknown>;
    body: unknown;
  };
  response?: {
    headers: Record<string, unknown>;
    body: unknown;
  };
  error?: unknown;
  stack?: string;
}

export interface HttpDevToolsApi {
  list: (count?: number) => HttpDevToolsPayload[];
  copy: (count?: number) => Promise<string>;
  copyLast: (count?: number) => Promise<string>;
  clear: () => void;
  enabled: () => { enabled: boolean; storageKey: string; maxEntries: number; defaultExportCount: number };
}

@Injectable({ providedIn: 'root' })
export class HttpDevToolsService {
  private readonly enabled = Boolean(environment.httpDevToolsEnabled);
  private readonly startedAtById = new Map<string, number>();

  constructor() {
    this.exposeConsoleApi();
  }

  start(entry: Omit<HttpDevToolsPayload, 'type' | 'timestamp' | 'pending'>): string {
    if (!this.enabled) {
      return entry.id;
    }

    this.startedAtById.set(entry.id, Date.now());
    this.upsert({
      ...entry,
      type: 'http',
      timestamp: new Date().toISOString(),
      pending: true,
    });

    return entry.id;
  }

  complete(id: string, response: HttpResponse<unknown>): void {
    if (!this.enabled) {
      return;
    }

    const durationMs = this.resolveDuration(id);
    this.patchEntry(id, {
      durationMs,
      pending: false,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      response: {
        headers: this.headersToRecord(response.headers),
        body: this.sanitizeValue(response.body),
      },
    });
  }

  fail(id: string, error: unknown): void {
    if (!this.enabled) {
      return;
    }

    const durationMs = this.resolveDuration(id);
    const httpError = error instanceof HttpErrorResponse ? error : null;

    this.patchEntry(id, {
      durationMs,
      pending: false,
      status: httpError?.status,
      statusText: httpError?.statusText,
      ok: false,
      error: this.sanitizeValue(this.toErrorPayload(error)),
    });
  }

  captureException(source: string, error: unknown): void {
    if (!this.enabled) {
      return;
    }

    const errorPayload = this.toErrorPayload(error);
    const stack = error instanceof Error ? error.stack : undefined;

    this.upsert({
      type: 'exception',
      id: createEntryId(),
      timestamp: new Date().toISOString(),
      pending: false,
      source,
      ok: false,
      error: this.sanitizeValue(errorPayload),
      stack: stack ? String(this.sanitizeValue(stack)) : undefined,
    });
  }

  registerGlobalErrorListeners(): void {
    if (!this.enabled || typeof window === 'undefined' || window.__ariaHttpDevToolsGlobalErrorsRegistered) {
      return;
    }

    window.__ariaHttpDevToolsGlobalErrorsRegistered = true;
    window.addEventListener('error', (event) => {
      this.captureException('window.error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });
    });
    window.addEventListener('unhandledrejection', (event) => {
      this.captureException('window.unhandledrejection', event.reason);
    });
  }

  list(count = HTTP_DEVTOOLS_DEFAULT_EXPORT_COUNT): HttpDevToolsPayload[] {
    return this.readEntries().slice(-this.normalizeCount(count));
  }

  async copy(count = HTTP_DEVTOOLS_DEFAULT_EXPORT_COUNT): Promise<string> {
    const payload = JSON.stringify(this.list(count), null, 2);

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(payload);
      } catch {
        return payload;
      }
    }

    return payload;
  }

  clear(): void {
    if (!this.hasLocalStorage()) {
      return;
    }

    window.localStorage.removeItem(HTTP_DEVTOOLS_STORAGE_KEY);
  }

  enabledInfo(): { enabled: boolean; storageKey: string; maxEntries: number; defaultExportCount: number } {
    return {
      enabled: this.enabled,
      storageKey: HTTP_DEVTOOLS_STORAGE_KEY,
      maxEntries: HTTP_DEVTOOLS_MAX_ENTRIES,
      defaultExportCount: HTTP_DEVTOOLS_DEFAULT_EXPORT_COUNT,
    };
  }

  headersToRecord(headers: HttpHeaders): Record<string, unknown> {
    return headers.keys().reduce<Record<string, unknown>>((acc, key) => {
      const values = headers.getAll(key) ?? [];
      acc[key] = this.isSensitiveKey(key) ? REDACTED : this.flattenValues(values);
      return acc;
    }, {});
  }

  paramsToRecord(params: HttpParams): Record<string, unknown> {
    return params.keys().reduce<Record<string, unknown>>((acc, key) => {
      const values = params.getAll(key) ?? [];
      acc[key] = this.isSensitiveKey(key) ? REDACTED : this.flattenValues(values);
      return acc;
    }, {});
  }

  sanitizeValue(value: unknown): unknown {
    return this.truncateValue(this.sanitizeRecursive(value, new WeakSet<object>()));
  }

  private exposeConsoleApi(): void {
    if (!this.enabled || typeof window === 'undefined') {
      return;
    }

    window.ariaHttpDevTools = {
      list: (count = HTTP_DEVTOOLS_DEFAULT_EXPORT_COUNT) => this.list(count),
      copy: (count = HTTP_DEVTOOLS_DEFAULT_EXPORT_COUNT) => this.copy(count),
      copyLast: (count = HTTP_DEVTOOLS_DEFAULT_EXPORT_COUNT) => this.copy(count),
      clear: () => this.clear(),
      enabled: () => this.enabledInfo(),
    };
  }

  private upsert(entry: HttpDevToolsPayload): void {
    const entries = this.readEntries().filter((item) => item.id !== entry.id);
    entries.push(entry);
    this.writeEntries(entries);
  }

  private patchEntry(id: string, patch: Partial<HttpDevToolsPayload>): void {
    const entries = this.readEntries();
    const index = entries.findIndex((entry) => entry.id === id);

    if (index < 0) {
      return;
    }

    entries[index] = { ...entries[index], ...patch };
    this.writeEntries(entries);
  }

  private readEntries(): HttpDevToolsPayload[] {
    if (!this.hasLocalStorage()) {
      return [];
    }

    const rawValue = window.localStorage.getItem(HTTP_DEVTOOLS_STORAGE_KEY);

    if (!rawValue) {
      return [];
    }

    try {
      const parsed = JSON.parse(rawValue);
      return Array.isArray(parsed) ? parsed as HttpDevToolsPayload[] : [];
    } catch {
      return [];
    }
  }

  private writeEntries(entries: HttpDevToolsPayload[]): void {
    if (!this.hasLocalStorage()) {
      return;
    }

    const trimmedEntries = entries.slice(-HTTP_DEVTOOLS_MAX_ENTRIES);

    try {
      window.localStorage.setItem(HTTP_DEVTOOLS_STORAGE_KEY, JSON.stringify(trimmedEntries));
    } catch {
      try {
        window.localStorage.setItem(HTTP_DEVTOOLS_STORAGE_KEY, JSON.stringify(trimmedEntries.slice(-HTTP_DEVTOOLS_DEFAULT_EXPORT_COUNT)));
      } catch {
        window.localStorage.removeItem(HTTP_DEVTOOLS_STORAGE_KEY);
      }
    }
  }

  private resolveDuration(id: string): number {
    const startedAt = this.startedAtById.get(id);
    this.startedAtById.delete(id);
    return startedAt ? Date.now() - startedAt : 0;
  }

  private normalizeCount(count: number): number {
    if (!Number.isFinite(count) || count < 1) {
      return HTTP_DEVTOOLS_DEFAULT_EXPORT_COUNT;
    }

    return Math.min(Math.floor(count), HTTP_DEVTOOLS_MAX_ENTRIES);
  }

  private flattenValues(values: string[]): string | string[] | null {
    if (values.length === 0) {
      return null;
    }

    return values.length === 1 ? values[0] : values;
  }

  private sanitizeRecursive(value: unknown, seen: WeakSet<object>): unknown {
    if (value === null || value === undefined || typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') {
      return value;
    }

    if (value instanceof Blob) {
      return `[${value.constructor.name}: ${value.size} bytes]`;
    }

    if (value instanceof ArrayBuffer) {
      return `[ArrayBuffer: ${value.byteLength} bytes]`;
    }

    if (ArrayBuffer.isView(value)) {
      return `[${value.constructor.name}: ${value.byteLength} bytes]`;
    }

    if (value instanceof FormData) {
      return this.formDataToRecord(value);
    }

    if (typeof value !== 'object') {
      return String(value);
    }

    if (seen.has(value)) {
      return '[circular]';
    }

    seen.add(value);

    if (Array.isArray(value)) {
      return value.map((item) => this.sanitizeRecursive(item, seen));
    }

    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, item]) => {
      acc[key] = this.isSensitiveKey(key) ? REDACTED : this.sanitizeRecursive(item, seen);
      return acc;
    }, {});
  }

  private formDataToRecord(formData: FormData): Record<string, unknown> {
    const output: Record<string, unknown> = {};

    formData.forEach((value, key) => {
      output[key] = this.isSensitiveKey(key)
        ? REDACTED
        : value instanceof File
          ? `[File: ${value.name}, ${value.size} bytes]`
          : value;
    });

    return output;
  }

  private truncateValue(value: unknown): unknown {
    const serialized = JSON.stringify(value);

    if (!serialized || serialized.length <= MAX_SECTION_LENGTH) {
      return value;
    }

    return `${serialized.slice(0, MAX_SECTION_LENGTH)}...${TRUNCATED}`;
  }

  private toErrorPayload(error: unknown): unknown {
    if (error instanceof HttpErrorResponse) {
      return {
        name: error.name,
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        error: error.error,
      };
    }

    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
      };
    }

    return error;
  }

  private isSensitiveKey(key: string): boolean {
    const normalizedKey = key.toLowerCase().replace(/[-_]/g, '');
    return SENSITIVE_KEYS.some((sensitiveKey) => normalizedKey.includes(sensitiveKey.replace(/[-_]/g, '')));
  }

  private hasLocalStorage(): boolean {
    return typeof window !== 'undefined' && Boolean(window.localStorage);
  }
}

function createEntryId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
